import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ClipboardCheck, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: '',
    company_name: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.role) {
      toast.error('Veuillez sélectionner un rôle');
      return;
    }

    if (formData.role === 'sme' && !formData.company_name) {
      toast.error('Le nom de l\'entreprise est requis pour les comptes PME');
      return;
    }

    setLoading(true);

    try {
      const userData = {
        ...formData,
        company_name: formData.role === 'sme' ? formData.company_name : null
      };
      const user = await register(userData);
      toast.success(`Bienvenue, ${user.name} ! Compte créé avec succès.`);
      
      if (user.role === 'admin') {
        navigate('/admin');
      } else if (user.role === 'consultant') {
        navigate('/consultant');
      } else {
        navigate('/pme');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Échec de l\'inscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Formulaire */}
      <div className="flex items-center justify-center p-8 lg:p-12">
        <div className="w-full max-w-md space-y-8">
          <div className="flex items-center gap-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_consult-tracker-14/artifacts/zvf1f82s_logo-ccitogo.png" 
              alt="CCI Togo" 
              className="h-12 w-auto"
            />
            <span className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">CCI Togo</span>
          </div>

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="space-y-1 pb-6">
              <CardTitle className="text-2xl font-semibold text-slate-900">Créer un compte</CardTitle>
              <CardDescription className="text-slate-500">
                Choisissez votre rôle et commencez
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-slate-700 font-medium">Nom complet</Label>
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jean Dupont"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    data-testid="register-name-input"
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="vous@entreprise.com"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    data-testid="register-email-input"
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone" className="text-slate-700 font-medium">Téléphone</Label>
                  <Input
                    id="phone"
                    type="tel"
                    placeholder="+33 6 12 34 56 78"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    data-testid="register-phone-input"
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700 font-medium">Mot de passe</Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    required
                    minLength={6}
                    data-testid="register-password-input"
                    className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Rôle</Label>
                  <Select 
                    value={formData.role} 
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                  >
                    <SelectTrigger data-testid="register-role-select" className="h-11 border-slate-200">
                      <SelectValue placeholder="Sélectionnez votre rôle" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="consultant" data-testid="role-consultant">Consultant</SelectItem>
                      <SelectItem value="sme" data-testid="role-sme">PME (Dirigeant)</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500">
                    Les comptes administrateur sont créés par un administrateur existant.
                  </p>
                </div>

                {formData.role === 'sme' && (
                  <div className="space-y-2 fade-in">
                    <Label htmlFor="company_name" className="text-slate-700 font-medium">Nom de l'entreprise</Label>
                    <Input
                      id="company_name"
                      type="text"
                      placeholder="Votre Entreprise SARL"
                      value={formData.company_name}
                      onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                      required
                      data-testid="register-company-input"
                      className="h-11 border-slate-200 focus:ring-2 focus:ring-slate-900 focus:border-transparent"
                    />
                  </div>
                )}

                <Button 
                  type="submit" 
                  disabled={loading}
                  data-testid="register-submit-btn"
                  className="w-full h-11 bg-slate-900 hover:bg-slate-800 text-white font-medium"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full"></span>
                      Création...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <UserPlus className="w-4 h-4" />
                      Créer le compte
                    </span>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          <p className="text-center text-slate-600">
            Déjà un compte ?{' '}
            <Link to="/login" className="text-orange-500 hover:text-orange-600 font-medium" data-testid="login-link">
              Se connecter
            </Link>
          </p>
        </div>
      </div>

      {/* Image */}
      <div 
        className="hidden lg:block bg-cover bg-center relative"
        style={{ backgroundImage: 'url(https://images.pexels.com/photos/4063856/pexels-photo-4063856.jpeg)' }}
      >
        <div className="absolute inset-0 bg-slate-900/60 flex items-end p-12">
          <div className="text-white max-w-lg">
            <h2 className="text-3xl font-bold mb-4 font-['Plus_Jakarta_Sans']">
              Rejoignez la plateforme
            </h2>
            <p className="text-slate-200 text-lg leading-relaxed">
              Que vous soyez consultant suivant votre travail ou PME validant des services, 
              nous vous fournissons les outils nécessaires pour une collaboration transparente.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
